import { useInView } from "@/hooks/useInView";
import { TrendingUp, Radio, CalendarDays, MapPin } from "lucide-react";

const features = [
  {
    icon: TrendingUp,
    title: "Dynamiczny rozwój sieci 5G w Polsce",
    desc: "Polska jest zobowiązana do pokrycia siecią 5G 95% terytorium do 2030 roku. Operatorzy intensywnie poszukują nowych lokalizacji pod stacje bazowe w każdym województwie.",
    color: "emerald",
  },
  {
    icon: Radio,
    title: "Masowe zapotrzebowanie na nowe lokalizacje",
    desc: "Sieć 5G wymaga znacznie gęstszego rozmieszczenia stacji BTS niż poprzednie generacje. Każda odpowiednia działka przy drodze, wsi lub strefie przemysłowej ma realny potencjał.",
    color: "emerald",
  },
  {
    icon: CalendarDays,
    title: "Stabilność na dekady",
    desc: "Maszt BTS pozostaje w eksploatacji przez cały okres umowy — początkowo 10 lat, z możliwością przedłużenia aneksem do maksymalnie 29 lat. Twoja działka staje się wieloletnim, niezawodnym źródłem dochodu pasywnego.",
    color: "emerald",
  },
  {
    icon: MapPin,
    title: "Twoja lokalizacja może się kwalifikować",
    desc: "Wolnostojące grunty, dachy hal magazynowych i produkcyjnych, pola przy drogach krajowych – każda lokalizacja wymaga indywidualnej oceny. Bezpłatny audyt wskaże, czy Twoja nieruchomość spełnia kryteria.",
    color: "amber",
  },
];

const stats = [
  { value: "2030", label: "Termin pokrycia 5G w Polsce" },
  { value: "3×", label: "Gęstsza sieć niż 4G" },
  { value: "95%", label: "Wymagane pokrycie terytorium" },
  { value: "do 29 lat", label: "Maksymalna długość umowy" },
];

export default function Comparison() {
  const [ref, inView] = useInView(0.15);

  return (
    <section
      id="dlaczego-5g"
      className="py-24 bg-[#FAFAFA]"
      data-testid="why-bts-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-600 text-sm font-semibold uppercase tracking-widest">
            Rewolucja 5G
          </span>
          <h2
            className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight"
          >
            Polska buduje sieć 5G –{" "}
            <br className="hidden sm:block" />
            lokalizacje są kluczowe
          </h2>
          <p className="text-slate-500 text-lg mt-4 max-w-2xl mx-auto">
            Operatorzy telekomunikacyjni są zobowiązani do pokrycia Polski siecią
            5G do 2030 roku. To wymaga tysięcy nowych stacji bazowych BTS –
            właśnie teraz intensywnie poszukujemy nowych lokalizacji.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-14">
          {features.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </div>

        <div
          className={`bg-emerald-700 rounded-2xl p-8 grid grid-cols-2 sm:grid-cols-4 gap-8 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "400ms" }}
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="text-center"
              data-testid={`stat-5g-${i + 1}`}
            >
              <div
                className="text-3xl font-black text-white number-highlight"
                  >
                {s.value}
              </div>
              <div className="text-emerald-100 text-sm mt-1 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }) {
  const [ref, inView] = useInView();
  const Icon = feature.icon;

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl p-7 border border-slate-200 shadow-xl shadow-slate-900/5 hover:shadow-emerald-900/10 hover:-translate-y-1 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`feature-card-${index + 1}`}
    >
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
          feature.color === "amber" ? "bg-amber-100" : "bg-emerald-100"
        }`}
      >
        <Icon
          className={`w-6 h-6 ${
            feature.color === "amber" ? "text-amber-600" : "text-emerald-600"
          }`}
        />
      </div>
      <h3 className="font-bold text-slate-900 text-lg mb-3">
        {feature.title}
      </h3>
      <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
    </div>
  );
}
